import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../providers/ThemeContext";
import { useI18n } from "../../languages/providers/I18nProvider";

export type CreationTheme = {
  background: string;
  card?: string;
  text: string;
  muted?: string; // used for the light grey title
  border: string;
};

export type TrustedLogo = {
  src: string;
  alt: string;
  href?: string; // external
  to?: string; // internal route
  width?: number;
  height?: number;
};

export type TrustedByProps = {
  /** If missing/empty/whitespace, the title row is hidden */
  title?: string | null;
  logos: ReadonlyArray<TrustedLogo>;
  height?: number; // logo box height (px)
  speedSec?: number; // marquee loop duration
  gap?: number; // gap between logos (px)
  disableMarquee?: boolean; // force grid
  grayscale?: boolean; // render logos B&W
  hoverRevealColor?: boolean; // if grayscale, reveal color on hover
  showTopBorder?: boolean; // default: false
  showBottomBorder?: boolean; // default: false
  className?: string;
};

const edgeFadeMask = (dir: "ltr" | "rtl") =>
  dir === "rtl"
    ? "linear-gradient(to left, transparent, black 6%, black 94%, transparent)"
    : "linear-gradient(to right, transparent, black 6%, black 94%, transparent)";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function TrustedBy({
  title,
  logos,
  height = 85,
  speedSec = 40,
  gap = 32,
  disableMarquee,
  grayscale = true,
  hoverRevealColor = true,
  showTopBorder = false,
  showBottomBorder = false,
  className,
}: TrustedByProps) {
  const { theme } = useTheme();
  // Direction must be reactive: on a client-side language switch the component
  // re-renders without remounting, so reading the DOM once on mount left the
  // marquee stale (broken until refresh). useI18n().direction updates on switch.
  const { direction: dir } = useI18n();
  const reduced = prefersReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);

  // Duplicate to form a seamless track
  const doubled = useMemo(() => [...logos, ...logos], [logos]);

  // Pause marquee when offscreen
  useEffect(() => {
    if (!trackRef.current || reduced || disableMarquee) return;
    const el = trackRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          (el.style as any).animationPlayState = e.isIntersecting
            ? "running"
            : "paused";
        });
      },
      { threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [reduced, disableMarquee]);

  const isMobile =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(max-width: 768px)").matches
      : false;

  const H = height;
  const GAP = isMobile ? Math.min(20, gap) : gap;
  const SPEED = Math.max(10, speedSec);

  const baseBg = "#fff";

  // Infinite marquee: translate exactly 50% of the doubled track
  const distance = dir === "rtl" ? "50%" : "-50%";
  const css = `
@keyframes ca-marquee-${H}-${dir} {
  from { transform: translateX(0); }
  to   { transform: translateX(${distance}); }
}
`;

  return (
    <section
      className={className}
      aria-label="Trusted by"
      style={{
        position: "relative",
        isolation: "isolate" as const,
        padding: isMobile ? "18px 0" : "22px 0",
        background: baseBg,
        borderTop: showTopBorder ? `1px solid ${theme.border}` : "none",
        borderBottom: showBottomBorder ? `1px solid ${theme.border}` : "none",
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />

      {/* Title (like the screenshot: Poppins, muted grey, subtle) */}
      {typeof title === "string" && title.trim().length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingInline: isMobile ? 16 : 24,
            marginBottom: isMobile ? 12 : 14,
            textAlign: "center",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: "'Poppins', sans-serif",
              fontSize: 14,
              fontWeight: 500,
              color: theme.status,
            }}
          >
            {title}
          </h2>
        </div>
      )}

      {/* Logos */}
      <div
        ref={containerRef}
        style={{
          position: "relative",
          overflow: "hidden",
          paddingInline: isMobile ? 12 : 18,
          WebkitMaskImage: edgeFadeMask(dir),
          maskImage: edgeFadeMask(dir),
        }}
      >
        {reduced || disableMarquee ? (
          <ul
            role="list"
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexWrap: "wrap",
              gap: GAP,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {logos.map((l, i) => (
              <LogoItem
                key={i}
                logo={l}
                height={H}
                grayscale={grayscale}
                hoverRevealColor={hoverRevealColor}
              />
            ))}
          </ul>
        ) : (
          <ul
            ref={trackRef}
            role="list"
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: GAP,
              width: "max-content",
              animation: `ca-marquee-${H}-${dir} ${SPEED}s linear infinite`,
              willChange: "transform",
              flexDirection:
                dir === "rtl" ? ("row-reverse" as const) : ("row" as const),
            }}
          >
            {doubled.map((l, i) => (
              <LogoItem
                key={i}
                // The second copy only exists for the seamless loop: hide it
                // from assistive tech and the tab order.
                duplicate={i >= logos.length}
                logo={l}
                height={H}
                grayscale={grayscale}
                hoverRevealColor={hoverRevealColor}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function LogoItem({
  logo,
  height,
  grayscale,
  hoverRevealColor,
  duplicate = false,
}: {
  duplicate?: boolean;
  logo: TrustedLogo;
  height: number;
  grayscale: boolean;
  hoverRevealColor: boolean;
}) {
  const content = (
    <div
      style={{
        height,
        minHeight: height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingInline: 8,
        borderRadius: 8,
        boxSizing: "border-box",
      }}
    >
      <img
        src={logo.src}
        alt={logo.alt}
        loading="lazy"
        decoding="async"
        width={logo.width}
        height={logo.height}
        style={{
          maxHeight: height - 8,
          height: "auto",
          width: "auto",
          maxWidth: 180,
          objectFit: "contain",
          filter: grayscale ? "grayscale(100%) contrast(1.05)" : "none",
          opacity: grayscale ? 0.9 : 1,
          transition: "opacity .2s ease, filter .2s ease, transform .2s ease",
        }}
        onMouseEnter={(e) => {
          if (grayscale && hoverRevealColor) {
            e.currentTarget.style.opacity = "1";
            e.currentTarget.style.filter = "grayscale(0%) contrast(1)";
            e.currentTarget.style.transform = "scale(1.1)";
          } else {
            e.currentTarget.style.transform = "scale(1.1)";
          }
        }}
        onMouseLeave={(e) => {
          if (grayscale && hoverRevealColor) {
            e.currentTarget.style.opacity = "0.9";
            e.currentTarget.style.filter = "grayscale(100%) contrast(1.05)";
            e.currentTarget.style.transform = "scale(1.0)";
          } else {
            e.currentTarget.style.transform = "scale(1.0)";
          }
        }}
      />
    </div>
  );

  const liProps = duplicate ? { "aria-hidden": true as const } : {};
  const tab = duplicate ? { tabIndex: -1 } : {};

  if (logo.to) {
    return (
      <li style={{ display: "flex", alignItems: "center" }} {...liProps}>
        <Link
          {...tab}
          to={logo.to}
          aria-label={logo.alt}
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          {content}
        </Link>
      </li>
    );
  }
  if (logo.href) {
    return (
      <li style={{ display: "flex", alignItems: "center" }} {...liProps}>
        <a
          {...tab}
          href={logo.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={logo.alt}
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          {content}
        </a>
      </li>
    );
  }
  return (
    <li style={{ display: "flex", alignItems: "center" }} {...liProps}>
      {content}
    </li>
  );
}
